import React from 'react';
import UIButton from './atoms/UIButton';
import UIInput from './atoms/UIInput';
import UIIcon from './atoms/UIIcon';
import UISearchBar from './molecules/UISearchBar';
import UIFormField from './molecules/UIFormField';
import UIMobileNav from './organisms/UIMobileNav';
import UIDesktopNav from './organisms/UIDesktopNav';
import UIListItem from './organisms/UIListItem';
import UICard from './organisms/UICard';

/**
 * Maps component type to actual React component
 */
const COMPONENT_MAP = {
    // Atoms
    button: UIButton,
    input: UIInput,
    icon: UIIcon,
    // Molecules
    searchBar: UISearchBar,
    formField: UIFormField,
    // Organisms
    mobileNav: UIMobileNav,
    desktopNav: UIDesktopNav,
    listItem: UIListItem,
    card: UICard
};

/**
 * UIComponentRenderer - Renders the appropriate component based on type
 */
const UIComponentRenderer = ({
    componentType,
    state = 'default',
    contentOverrides = {},
    deviceType = 'mobile',
    designKit
}) => {
    const Component = COMPONENT_MAP[componentType];

    // If it's a known component, render it directly
    if (Component) {
        const kitComponent = designKit?.components?.atoms?.find(c => c.type === componentType)
            || designKit?.components?.molecules?.find(c => c.type === componentType)
            || designKit?.components?.organisms?.find(c => c.type === componentType);

        const defaultProps = kitComponent?.props || {};

        return (
            <Component
                {...defaultProps}
                {...contentOverrides}
                state={state}
                deviceType={deviceType}
                tokens={designKit?.tokens}
            />
        );
    }

    // Check if it's a custom user-created component in the design kit
    const customComponent = designKit?.components?.atoms?.find(c => c.type === componentType)
        || designKit?.components?.molecules?.find(c => c.type === componentType)
        || designKit?.components?.organisms?.find(c => c.type === componentType);

    if (customComponent && customComponent.children && customComponent.children.length > 0) {
        // Render the custom molecule/organism with its composed children
        const { autoLayout = {}, spacingConfig = {}, children } = customComponent;

        const getPx = (tokenName) => {
            if (!tokenName || tokenName === '0' || tokenName === 'space-0') return 0;
            if (typeof tokenName === 'number') return tokenName;
            const token = designKit?.tokens?.spacing?.find(t => t.name === tokenName);
            return token?.values?.[deviceType] || token?.values?.mobile || parseInt(tokenName, 10) || 0;
        };

        return (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: autoLayout.direction === 'row' ? 'row' : 'column',
                    alignItems: autoLayout.align === 'start' ? 'flex-start' :
                        autoLayout.align === 'end' ? 'flex-end' :
                            autoLayout.align === 'center' ? 'center' : 'stretch',
                    justifyContent: autoLayout.justify === 'start' ? 'flex-start' :
                        autoLayout.justify === 'end' ? 'flex-end' :
                            autoLayout.justify === 'center' ? 'center' :
                                autoLayout.justify === 'space-between' ? 'space-between' :
                                    autoLayout.justify === 'space-around' ? 'space-around' : 'flex-start',
                    gap: getPx(autoLayout.gap),
                    padding: `${getPx(spacingConfig?.padding?.top)}px ${getPx(spacingConfig?.padding?.right)}px ${getPx(spacingConfig?.padding?.bottom)}px ${getPx(spacingConfig?.padding?.left)}px`,
                    background: customComponent.isCustom ? 'rgba(126, 231, 135, 0.05)' : undefined,
                    borderRadius: 4
                }}
            >
                {children.map((child, idx) => {
                    const childId = typeof child === 'string' ? child : child.componentId;
                    const childType = typeof child === 'string' ? null : child.componentType;
                    const childVariant = typeof child === 'string' ? 'default' : (child.variant || 'default');
                    const childWidth = typeof child === 'object' ? child.width : undefined;
                    const childHeight = typeof child === 'object' ? child.height : undefined;

                    // Find the child component type
                    const childComponent = designKit?.components?.atoms?.find(a => a.id === childId || a.type === childType)
                        || designKit?.components?.molecules?.find(m => m.id === childId || m.type === childType);

                    const actualType = childComponent?.type || childType || childId;

                    return (
                        <div
                            key={idx}
                            style={{
                                flex: autoLayout.align === 'stretch' && !childWidth ? 1 : 'none',
                                width: childWidth,
                                height: childHeight,
                                minWidth: 0
                            }}
                        >
                            <UIComponentRenderer
                                componentType={actualType}
                                state={state}
                                contentOverrides={{ variant: childVariant }}
                                deviceType={deviceType}
                                designKit={designKit}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    // Fallback for unknown component types with no composition
    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                minHeight: 40,
                background: 'rgba(255, 166, 87, 0.1)',
                border: '1px dashed #ffa657',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffa657',
                fontSize: 12
            }}
        >
            {componentType || 'Unknown'}
        </div>
    );
};

export default UIComponentRenderer;
